import React from 'react';
import s from '../DetailModal.module.scss';

/**
 * 文字总结内容组件
 * 用于展示 Markdown 格式的文本内容
 */
const TextSummaryContent = ({ data }) => {
    // 解析行内格式（加粗 ** 和数学公式 \\( \\)）
    const parseInlineFormatting = (text) => {
        const elements = [];
        let currentIndex = 0;
        
        // 正则匹配 **加粗** 和 \\(...\\) 数学公式
        const regex = /(\*\*.*?\*\*)|(\\\(.*?\\\))/g;
        let match;
        
        while ((match = regex.exec(text)) !== null) {
            // 添加匹配前的普通文本
            if (match.index > currentIndex) {
                elements.push(text.substring(currentIndex, match.index));
            }
            
            // 处理加粗文本
            if (match[1]) {
                const boldText = match[1].replace(/\*\*/g, '');
                elements.push(<strong key={match.index}>{boldText}</strong>);
            }
            // 处理数学公式
            else if (match[2]) {
                const mathText = match[2].replace(/\\\(|\\\)/g, '');
                elements.push(
                    <span key={match.index} className={s['math-inline']}>
                        {mathText}
                    </span>
                );
            }
            
            currentIndex = match.index + match[0].length;
        }
        
        // 添加剩余的文本
        if (currentIndex < text.length) {
            elements.push(text.substring(currentIndex));
        }
        
        return elements.length > 0 ? elements : text;
    };

    // 解析并渲染 Markdown 内容
    const renderContent = (content) => {
        if (!content) return null;
        
        // 按行分割内容，保留空行用于段落分隔
        const lines = content.split('\n');
        let isFirstH1 = true; // 跟踪是否是第一个一级标题
        
        return lines.map((line, index) => {
            const trimmedLine = line.trim();
            
            // 跳过空行
            if (!trimmedLine) {
                return null;
            }
            
            // Markdown 一级标题 # 
            if (trimmedLine.startsWith('# ')) {
                let titleText = trimmedLine.substring(2);
                // 去掉【标题：】、标题：、标题等前缀
                titleText = titleText.replace(/^【标题：】|^【标题:】|^标题：|^标题:|^标题\s+/, '');
                titleText = titleText.trim();
                
                // 第一个一级标题用 md-h1，其他用 md-h1-other
                const className = isFirstH1 ? s['md-h1'] : s['md-h1-other'];
                isFirstH1 = false; // 标记已遇到第一个一级标题
                
                return (
                    <h1 key={index} className={className}>
                        <strong>{titleText}</strong>
                    </h1>
                );
            }
            
            // Markdown 二级标题 ##
            if (trimmedLine.startsWith('## ')) {
                let titleText = trimmedLine.substring(3);
                // 去掉【标题：】、标题：、标题等前缀
                titleText = titleText.replace(/^【标题：】|^【标题:】|^标题：|^标题:|^标题\s+/, '');
                // 对于主题：，只去掉冒号，保留"主题"
                titleText = titleText.replace(/^主题：|^主题:/, '主题');
                titleText = titleText.trim();
                return (
                    <h2 key={index} className={s['md-h2']}>
                        <strong>{titleText}</strong>
                    </h2>
                );
            }
            
            // Markdown 三级标题 ###
            if (trimmedLine.startsWith('### ')) {
                let titleText = trimmedLine.substring(4);
                // 去掉【标题：】、标题：、标题等前缀
                titleText = titleText.replace(/^【标题：】|^【标题:】|^标题：|^标题:|^标题\s+/, '');
                // 对于主题：，只去掉冒号，保留"主题"
                titleText = titleText.replace(/^主题：|^主题:/, '主题');
                titleText = titleText.trim();
                return (
                    <h3 key={index} className={s['md-h3']}>
                        <strong>{titleText}</strong>
                    </h3>
                );
            }
            
            // 检测是否为列表项（以数字开头，如 1.、2. 或 • 开头）
            if (/^[0-9]+\./.test(trimmedLine)) {
                // 去掉数字前缀（如 "1. " 或 "12. "）
                let listText = trimmedLine.replace(/^[0-9]+\.\s*/, '');
                // 处理列表项中的加粗和数学公式
                const content = parseInlineFormatting(listText);
                return (
                    <div key={index} className={s['list-item']}>
                        {content}
                    </div>
                );
            }
            
            if (trimmedLine.startsWith('• ')) {
                return (
                    <div key={index} className={s['list-item']}>
                        {trimmedLine}
                    </div>
                );
            }
            
            // 普通段落（处理加粗和数学公式）
            const content = parseInlineFormatting(trimmedLine);
            return (
                <p key={index} className={s['section-text']}>
                    {content}
                </p>
            );
        });
    };

    return (
        <div className={s['content-main']}>
            {renderContent(data.description)}
        </div>
    );
};

export default TextSummaryContent;

